// src/components/Stores/Stores.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";

import Filter_Store from "../common/Filter_store";
import MobileFiltersDrawer from "../common/MobileFiltersDrawer";
import TimeRangePicker from "../common/TimeRangePicker";
import "./Stores.css";
import LangSwitcher from "../common/LangSwitcher";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { useDateRange } from "../../context/DateRangeContext";
import { useFilters } from "../../context/FilterContext";

/* ---------------- 常量 ---------------- */
const API_BASE = "https://boss.kfdtech.cn/api";
const PIE_COLORS = ["#7aa3ef", "#96d6a9", "#f2cf7d", "#b6b9c4", "#9aa0a6", "#60a5fa"];

/* ---------------- 小工具 ---------------- */
const toYm = (d) => {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};


const fmtShort = (n) => {
  const v = Number(n) || 0;
  const av = Math.abs(v);

  // < 1000 → 四舍五入成整数
  if (av < 1000) return String(Math.round(v));

  // ≥ 1000 → K/M/B 缩写
  const trim = (x) => x.toFixed(2).replace(/\.00$|(\.\d*[1-9])0+$/, "$1");

  if (av >= 1e9) return trim(v / 1e9) + "B";
  if (av >= 1e6) return trim(v / 1e6) + "M";
  if (av >= 1e3) return trim(v / 1e3) + "K";
};


/* ---------------- 城市环形图 ---------------- */
const CityDonutCard = ({ title, data }) => (
  <div className="city-card">
    <div className="city-card-header">{title}</div>
    <div className="city-card-body">
      <div className="donut-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius="62%"
              outerRadius="86%"
              paddingAngle={2}
              startAngle={90}
              endAngle={-270}
              stroke="#fff"
              strokeWidth={1.5}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(v, _n, p) => {
                const vol = p?.payload?.vol ?? 0;
                return [`${v}%`, `${p?.payload?.name} · ${fmtShort(vol)}`];
              }}
              labelFormatter={() => ""}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="legend-list">
        {data.map((d, i) => (
          <li key={d.name}>
            <span className="dot" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
            <span className="name">{d.name}</span>
            <span className="pct">{d.value}%</span>
            <span className="vol">{fmtShort(d.vol)}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/* ---------------- 表格 ---------------- */
const ROW_H = 42;
const PER_PAGE = 10;

const SortablePagedTable = ({ rows }) => {
  const [sortBy, setSortBy] = useState("openDate");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const { t } = useTranslation();

  const toggleSort = (key) => {
    setPage(1);
    setSortBy((prev) => (prev === key ? prev : key));
    setSortDir((prev) => (sortBy === key ? (prev === "asc" ? "desc" : "asc") : "desc"));
  };

  const sorted = useMemo(() => {
    const factor = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortBy === "amount") return factor * ((a.amount || 0) - (b.amount || 0));
      if (sortBy === "status") {
        const va = a.status === "OPEN" ? 1 : 0;
        const vb = b.status === "OPEN" ? 1 : 0;
        return factor * (va - vb);
      }
      const parseDate = (s) => {
        const t = Date.parse(s);
        return Number.isNaN(t) ? 0 : t;
      };
      return factor * (parseDate(a.openDate) - parseDate(b.openDate));
    });
  }, [rows, sortBy, sortDir]);

  const total = sorted.length;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));
  const safePage = Math.min(page, pages);
  const start = (safePage - 1) * PER_PAGE;
  const slice = sorted.slice(start, start + PER_PAGE);

  const blanks = Math.max(0, PER_PAGE - slice.length);
  const current = [...slice, ...Array.from({ length: blanks }, () => ({ __empty: true }))];

  const canPrev = safePage > 1;
  const canNext = safePage < pages;

  return (
    <div className="table-panel">
      <div className="h-scroll">
        <table className="store-table">
          <thead>
            <tr>
              <th>{t("storeName")}</th>
              <th>{t("Platform")}</th>
              <th onClick={() => toggleSort("openDate")} className="sortable">{t("OpenDate")}</th>
              <th onClick={() => toggleSort("amount")} className="sortable">{t("Overview_GMV_chat")}</th>
              <th onClick={() => toggleSort("status")} className="sortable">{t("Status")}</th>
            </tr>
          </thead>
          <tbody>
            {current.map((r, idx) =>
              r.__empty ? (
                <tr key={`empty-${idx}`} className="empty-row" style={{ height: ROW_H }}>
                  <td colSpan={5} />
                </tr>
              ) : (
                <tr key={`${r.store}-${r.platform}-${idx}`} style={{ height: ROW_H }}>
                  <td><span className="icon-diamond" />{r.store}</td>
                  <td>{r.platform}</td>
                  <td>{r.openDate}</td>
                  <td>{fmtShort(r.amount)}</td>
                  <td>
                    <span className={`status-pill ${r.status === "OPEN" ? "open" : "close"}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="pager">
          <div className="pager-info">
            {t("pageInfo", { current: safePage, total: pages })}
          </div>
          <div className="pager-controls">
            <button
              className="pager-btn"
              disabled={!canPrev}
              onClick={() => setPage((p) => p - 1)}
            >
              ‹ Prev
            </button>

            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                className={`pager-btn page-num ${n === safePage ? "active" : ""}`}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}

            <button
              className="pager-btn"
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
            >
              Next ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- 主页面 ---------------- */
export default function Stores() {
  const { authFetch } = useAuth();
  const { t } = useTranslation();
  const { dateRange, setDateRange } = useDateRange();
  const { filters, setFilters } = useFilters();

  const [filterOpts, setFilterOpts] = useState({ stores: [], platforms: [], brand2plats: {} });
  const initializedRef = useRef(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pies, setPies] = useState({ Beijing: [], Shanghai: [] });
  const [rows, setRows] = useState([]);

  const filterLoading =
    filterOpts.stores.length === 0 && filterOpts.platforms.length === 0;

  /* ---------------- 初始化 filters（与 Overview 完全相同逻辑） ---------------- */
  useEffect(() => {
    let abort = false;

    (async () => {
      try {
        const resp = await authFetch(`${API_BASE}/filters`);
        const json = await resp.json();
        if (!resp.ok)
          throw new Error(json?.detail || json?.error || `HTTP ${resp.status}`);
        if (abort) return;

        const stores = json.stores || [];
        const platforms = json.platforms || [];
        const brand2plats = json.brand2plats || {};
        setFilterOpts({ stores, platforms, brand2plats });

        // 只在 Stores 第一次加载时运行
        if (!initializedRef.current) {
          initializedRef.current = true;

          const contextHasFilters =
            (filters?.stores?.length ?? 0) > 0 ||
            (filters?.platforms?.length ?? 0) > 0;

          // 如果 Context 没有 filters → 自动全选
          if (!contextHasFilters) {
            const all = {
              stores: stores.slice(),
              platforms: platforms.slice()
            };
            setFilters(all);
            sessionStorage.setItem("filters", JSON.stringify(all));
          }
        }
      } catch (e) {
        if (!abort) setError(String(e?.message || e));
      }
    })();

    return () => {
      abort = true;
    };
  }, []);

  /* ---------------- 筛选改变 ---------------- */
  const onFiltersChange = (next) => {
    if (filterLoading) return;
    setFilters(next);
    sessionStorage.setItem("filters", JSON.stringify(next));
  };

  /* ---------------- 平台限制 ---------------- */
  const allowedPlatforms = useMemo(() => {
    const sel = filters.stores;
    if (!sel?.length) return filterOpts.platforms;

    const acc = new Set();
    sel.forEach((b) =>
      (filterOpts.brand2plats[b] || []).forEach((p) => acc.add(p))
    );
    return Array.from(acc).sort((a, b) => a.localeCompare(b));
  }, [filters.stores, filterOpts]);

  /* ---------------- 请求载荷 ---------------- */
  const payload = useMemo(() => {
    const start = toYm(dateRange?.startDate);
    const end = toYm(dateRange?.endDate);
    const brands = filters.stores || [];
    const plats = filters.platforms || [];

    if (!start || !end || brands.length === 0 || plats.length === 0)
      return null;

    return {
      YearMonth_List: [start, end],
      Brand_List: brands,
      Platform_List: plats,
      allow_current_month: dateRange?.mode === "custom",
    };
  }, [dateRange, filters]);

  /* ---------------- 数据请求 ---------------- */
  useEffect(() => {
    if (filterLoading || !payload) {
      setPies({ Beijing: [], Shanghai: [] });
      setRows([]);
      return;
    }

    let abort = false;

    (async () => {
      try {
        setLoading(true);

        const resp = await authFetch(`${API_BASE}/stores`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const json = await resp.json();
        if (!resp.ok)
          throw new Error(json?.detail || json?.error || `HTTP ${resp.status}`);
        if (abort) return;

        const bj = (json?.pies?.Beijing || []).map((d) => ({
          name: d.name,
          value: Number(d.pct ?? 0),
          vol: Number(d.netSales ?? 0),
        }));

        const sh = (json?.pies?.Shanghai || []).map((d) => ({
          name: d.name,
          value: Number(d.pct ?? 0),
          vol: Number(d.netSales ?? 0),
        }));

        const tbl = (json?.table || []).map((r) => ({
          store: r.store,
          platform: r.platform,
          openDate: r.openDate,
          amount: Number(r.amount || 0),
          status: r.status || "",
        }));

        setPies({ Beijing: bj, Shanghai: sh });
        setRows(tbl);
      } catch (e) {
        if (!abort) setError(String(e?.message || e));
      } finally {
        if (!abort) setLoading(false);
      }
    })();

    return () => {
      abort = true;
    };
  }, [payload, filterLoading]);

  return (
    <>
      <div className="stores-layout">
        <div className="main-panel">
          <div className="page-topbar">
            <h1 className="site-title">{t("title")}</h1>
            <div className="only-mobile"><LangSwitcher /></div>
          </div>

          <div className="overview-header">
            <div className="page-subtitle">{t("Stores")}</div>
            <div className="header-right">
              <button
                type="button"
                className="filter-button-mobile"
                onClick={() => setDrawerOpen(true)}
              >
                筛选
              </button>
              <TimeRangePicker value={dateRange} onChange={setDateRange} />
            </div>
          </div>

          <div className="overview-grid">
            <CityDonutCard title="Beijing" data={pies.Beijing} />
            <CityDonutCard title="Shanghai" data={pies.Shanghai} />
          </div>

          <h3 className="section-title">{t("All_Store_Status")}</h3>
          <SortablePagedTable rows={rows} />

          {loading && (
            <div className="loading-overlay">
              <div className="spinner" />Loading…
            </div>
          )}
          {error && !loading && (
            <div className="loading-overlay">Error: {error}</div>
          )}
        </div>

        <div className="filters-sidebar">
          <div className="filters-head"><LangSwitcher /></div>
          <Filter_Store
            loading={filterLoading}
            stores={filterOpts.stores}
            platforms={allowedPlatforms}
            value={filters}
            onChange={onFiltersChange}
          />
        </div>
      </div>

      <MobileFiltersDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Filter_Store
          loading={filterLoading}
          stores={filterOpts.stores}
          platforms={allowedPlatforms}
          value={filters}
          onChange={onFiltersChange}
        />
      </MobileFiltersDrawer>
    </>
  );
}
